import { z } from 'zod';
import { toKenyanMobile } from '../phone/kenyan-mobile';

/** How a sign-in code reaches a phone. WhatsApp comes first: it is the default wherever both are offered. */
export const LOGIN_CODE_CHANNELS = ['whatsapp', 'sms'] as const;
export type LoginCodeChannel = (typeof LOGIN_CODE_CHANNELS)[number];
export const loginCodeChannelSchema = z.enum(LOGIN_CODE_CHANNELS);

/**
 * `GET /auth/code-channels` (public): the channels a code can go by right now, in `LOGIN_CODE_CHANNELS` order. WhatsApp
 * is listed only when a WhatsApp provider is sending and has a login code template; empty when nothing can send.
 */
export interface LoginCodeChannels {
  channels: LoginCodeChannel[];
}

/**
 * How an office sign-in code can reach someone: the account's email, or its mobile by WhatsApp or SMS. Email comes
 * first: everyone has one.
 */
export const SIGN_IN_CODE_CHANNELS = ['email', ...LOGIN_CODE_CHANNELS] as const;
export type SignInCodeChannel = (typeof SIGN_IN_CODE_CHANNELS)[number];
export const signInCodeChannelSchema = z.enum(SIGN_IN_CODE_CHANNELS);

/**
 * `GET /auth/sign-in/channels` (public): the channels an office sign-in code can go by right now, in
 * `SIGN_IN_CODE_CHANNELS` order. Empty when nothing can send, and the login page offers only the password.
 */
export interface SignInCodeChannels {
  channels: SignInCodeChannel[];
}

/** What someone typed to say who they are: an email, or a Kenyan mobile in E.164. */
export type SignInIdentifier = { kind: 'email'; value: string } | { kind: 'phone'; value: string };

const EMAIL_SHAPE = z.email();

/** Reads "0712 345 678", "+254712345678" or "Wanjiru@Example.com"; anything else is null. Emails are lower-cased. */
export function parseSignInIdentifier(raw: string): SignInIdentifier | null {
  const text = raw.trim();
  if (text.includes('@')) return EMAIL_SHAPE.safeParse(text).success ? { kind: 'email', value: text.toLowerCase() } : null;
  const phone = toKenyanMobile(text);
  return phone ? { kind: 'phone', value: phone } : null;
}

const signInIdentifierSchema = z
  .string()
  .max(254)
  .transform((value, ctx) => {
    const identifier = parseSignInIdentifier(value);
    if (!identifier) ctx.addIssue({ code: 'custom', message: 'Enter an email or a Kenyan mobile number' });
    return identifier ?? z.NEVER;
  });

/** `POST /auth/sign-in/send-code`. Answers alike whether or not the email or number is anyone's. */
export const sendSignInCodeSchema = z.object({ identifier: signInIdentifierSchema, channel: signInCodeChannelSchema }).strict();
export type SendSignInCodeInput = z.input<typeof sendSignInCodeSchema>;
export type SendSignInCode = z.output<typeof sendSignInCodeSchema>;

/** `POST /auth/sign-in/verify`: a correct code is the whole sign-in, with no two-factor step after it. */
export const verifySignInCodeSchema = z
  .object({
    identifier: signInIdentifierSchema,
    code: z.string().regex(/^\d{6}$/, 'Expected the 6-digit code'),
  })
  .strict();
export type VerifySignInCodeInput = z.input<typeof verifySignInCodeSchema>;
export type VerifySignInCode = z.output<typeof verifySignInCodeSchema>;

/**
 * Response of a route that sends a code to someone. `displayedCode` is the code itself on a test deployment that shows
 * codes on screen (`EMAIL_CODE_DISPLAY_ENABLED`, refused on production); everywhere else it is null.
 */
export interface CodeSent {
  displayedCode: string | null;
}

/** The `reason` on a 401 from a code check. A number that belongs to nobody reads as `wrong_code`, never anything more telling. */
export const LOGIN_CODE_FAILURE_REASONS = ['wrong_code', 'expired', 'exhausted'] as const;
export type LoginCodeFailureReason = (typeof LOGIN_CODE_FAILURE_REASONS)[number];

/** The `reason` on a 429 from a send; `retryAfterSeconds` accompanies `too_soon`. */
export const LOGIN_CODE_SEND_REFUSALS = ['too_soon', 'limit_reached'] as const;
export type LoginCodeSendRefusal = (typeof LOGIN_CODE_SEND_REFUSALS)[number];
